import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';

export type TagInputSize = 'sm' | 'md' | 'lg';

export interface TagInputProps {
  /** Current tag values. */
  value?: ReadonlyArray<string>;
  defaultValue?: ReadonlyArray<string>;
  onChange?: (tags: ReadonlyArray<string>) => void;
  /** Characters that trigger tag commit. Default: `['Enter', ',']`. */
  commitKeys?: ReadonlyArray<string>;
  /** Strip whitespace before commit. Default true. */
  trim?: boolean;
  /** Reject commits that would duplicate an existing tag. Default true. */
  dedupe?: boolean;
  /** Max tag count — input disables when reached. */
  maxTags?: number;
  /** Validator; return `false` to reject the candidate before commit. */
  validate?: (candidate: string, current: ReadonlyArray<string>) => boolean;
  /** Placeholder shown when empty. */
  placeholder?: string;
  /** Tone channel for the chips. Default `neutral`. */
  tone?: 'neutral' | 'accent';
  size?: TagInputSize;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  className?: string;
}

/** Multi-value chip input — Gmail-style recipient field. Tags commit
 *  on Enter / comma (configurable) or on blur; Backspace on empty
 *  input removes the last tag.
 *
 *  Each chip is a button so keyboard users can Tab through existing
 *  tags and Delete/Backspace to remove. The hidden input is the
 *  primary focus target, so typing picks up wherever the user last
 *  left off regardless of which chip had focus. */
export function TagInput(props: TagInputProps): ReactNode {
  const {
    value,
    defaultValue,
    onChange,
    commitKeys = ['Enter', ','],
    trim = true,
    dedupe = true,
    maxTags,
    validate,
    placeholder,
    tone = 'neutral',
    size = 'md',
    disabled = false,
    invalid,
    className,
  } = props;

  const [tags, setTags] = useControllableState<ReadonlyArray<string>>({
    defaultValue: defaultValue ?? [],
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const atCap = maxTags !== undefined && tags.length >= maxTags;

  const commit = useCallback(
    (raw: string) => {
      const candidate = trim ? raw.trim() : raw;
      if (candidate === '') return;
      if (dedupe && tags.includes(candidate)) {
        setDraft('');
        return;
      }
      if (validate !== undefined && !validate(candidate, tags)) return;
      if (maxTags !== undefined && tags.length >= maxTags) return;
      setTags([...tags, candidate]);
      setDraft('');
    },
    [trim, dedupe, tags, validate, maxTags, setTags],
  );

  const removeAt = useCallback(
    (index: number) => {
      const next = tags.filter((_, i) => i !== index);
      setTags(next);
    },
    [tags, setTags],
  );

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.isComposing || e.key === 'Process') return;
      if (commitKeys.includes(e.key)) {
        e.preventDefault();
        commit(draft);
      } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
        e.preventDefault();
        removeAt(tags.length - 1);
      }
    },
    [commitKeys, commit, draft, tags, removeAt],
  );

  const onBlur = useCallback(() => {
    if (draft !== '') commit(draft);
  }, [commit, draft]);

  const onChipKey = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        removeAt(index);
        /* Return focus to the input for continuous typing UX. */
        inputRef.current?.focus();
      }
    },
    [removeAt],
  );

  const classes = useMemo(
    () =>
      [
        'vq-tag-input',
        `vq-tag-input--${size}`,
        `vq-tag-input--${tone}`,
        disabled && 'vq-tag-input--disabled',
        invalid === true && 'vq-tag-input--invalid',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [size, tone, disabled, invalid, className],
  );

  return (
    <div
      className={classes}
      onClick={() => {
        inputRef.current?.focus();
      }}
      data-component="TagInput"
      data-state={disabled ? 'disabled' : invalid === true ? 'invalid' : 'idle'}
    >
      {tags.map((t, i) => (
        <span key={`${t}-${String(i)}`} className="vq-tag-input__chip">
          <span className="vq-tag-input__label">{t}</span>
          <button
            type="button"
            className="vq-tag-input__remove"
            aria-label={`Remove ${t}`}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              removeAt(i);
            }}
            onKeyDown={(e) => {
              onChipKey(e, i);
            }}
          >
            ✕
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className="vq-tag-input__input"
        value={draft}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={tags.length === 0 ? placeholder : undefined}
        disabled={disabled || atCap}
        aria-label={props['aria-label'] ?? 'Tag input'}
        aria-invalid={invalid}
        spellCheck={false}
      />
    </div>
  );
}
